import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { usePagination } from "@components/index";
import {
  filterDataBySearch,
  generateGlobalSearchOptions,
} from "@utils/dataUtils";
import {
  formatDate,
  formatDateToYYYYMMDD,
  isValidDateRange,
} from "@utils/dateUtils";
import { fetchBookingList } from "@actions/fetchPoliciesAction";
import { useCSVExport } from "@hooks/useCSVExport";
import { MIS_FIELDS_BOOKINGS } from "@constants/misDownloadData";
import { useAppConfig } from "@src/context/AppConfigContext";

export const POLICY_STATUS = [
  { value: "active", label: "Active" },
  { value: "archived", label: "Inactive" },
];

export const searchOptions = generateGlobalSearchOptions(
  "Search across all booking data (PNR, Train, Customer, etc.)",
);

// Define searchable fields for bookings
const BOOKING_SEARCH_FIELDS = [
  "pnr_number",
  "train_number",
  "train_name",
  "customer_name",
  "mobile_number",
  "ticket_status",
];

/**
 * API service functions
 */
export const fetchBookingsData = async (urlParams) => {
  try {
    const response = await fetchBookingList(urlParams);

    return {
      success: true,
      data: response.data.policies,
      error: null,
    };
  } catch (err) {
    return {
      success: false,
      data: [],
      error: "Failed to fetch bookings",
    };
  }
};

/**
 * Filter function for bookings
 */
export const filterBookings = (bookings, searchValue) => {
  return filterDataBySearch(bookings, searchValue, BOOKING_SEARCH_FIELDS);
};

/**
 * Data mapper for MIS export
 */
const mapBookingToMISData = (booking, index) => ({
  "Sr No": index + 1,
  Date: booking.booking_datetime ? formatDate(booking.booking_datetime) : "-",
  "Transaction ID": booking.transaction_id || "-",
  PNR: booking.pnr_number || "-",
  "Train No": booking.train_number || "-",
  "Train Name": booking.train_name || "-",
  "Travel Date": booking.journey_date ? formatDate(booking.journey_date) : "-",
  Boarding: booking.from_station || "-",
  Destination: booking.to_station || "-",
  "Ticket Fare (₹)": booking.ticket_fare || "-",
  "Confirmation Probability at Sale (%)":
    booking.confirmation_probability || "-",
  "Product Issued (Y/N)": booking.train_secure_optin ? "Y" : "N",
  "Premium (₹)": booking.premium_amount || "-",
  Status: booking.ticket_status || "-",
});

/**
 * Custom hook for Bookings management
 */
export const useBookingsData = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("");

  const { selectedClient } = useAppConfig();

  const isInitialMount = useRef(true);

  // XLSX export hook
  const { exportToXLSX, generateDateFilename } = useCSVExport();

  // Load bookings data
  const loadBookingsData = useCallback(
    async (urlParams) => {
      setLoading(true);

      const params = `${urlParams}&clientname=${selectedClient?.clientName}`;

      const result = await fetchBookingsData(params);
      setBookings(result.data || []);
      setError(result.error);
      setLoading(false);
    },
    [selectedClient?.clientName],
  );

  // Initial data load
  useEffect(() => {
    if (isInitialMount.current) {
      const urlParams =
        "startDate=" +
        formatDateToYYYYMMDD(new Date()) +
        "&endDate=" +
        formatDateToYYYYMMDD(new Date());

      isInitialMount.current = false;
      loadBookingsData(urlParams);
    } else {
      if (
        (selectedStatusFilter || isValidDateRange(fromDate, toDate, true)) &&
        !isInitialMount.current
      ) {
        let urlParams = "";
        if (selectedStatusFilter) {
          urlParams = "status=" + selectedStatusFilter + "&";
        }

        if (isValidDateRange(fromDate, toDate, true)) {
          urlParams =
            urlParams +
            "startDate=" +
            formatDateToYYYYMMDD(fromDate) +
            "&endDate=" +
            formatDateToYYYYMMDD(toDate);
        }

        loadBookingsData(urlParams);
      }
    }
  }, [fromDate, toDate, selectedStatusFilter, loadBookingsData]);

  // Filter bookings based on search
  const filteredBookings = useMemo(() => {
    let filtered = filterBookings(bookings, searchValue);
    return filtered;
  }, [bookings, searchValue]);

  // Handle search
  const handleSearch = useCallback((_, value) => {
    setSearchValue(value);
  }, []);

  // Handle date range change
  const handleDateChange = useCallback((from, to) => {
    setFromDate(from);
    setToDate(to);
  }, []);

  // Handle booking view
  const handleViewBooking = useCallback((booking) => {
    setSelectedBooking(booking);
    setIsModalOpen(true);
  }, []);

  // Handle modal close
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedBooking(null);
  }, []);

  // Handle MIS data download using the XLSX export hook
  const handleDownloadMIS = useCallback(() => {
    const filename = generateDateFilename("MIS_Data_Policies");
    exportToXLSX(
      filteredBookings,
      MIS_FIELDS_BOOKINGS,
      filename,
      mapBookingToMISData,
      "Policies",
    );
  }, [filteredBookings, exportToXLSX, generateDateFilename]);

  // Pagination functionality
  const {
    currentPage,
    currentData: paginatedBookings,
    handlePageChange,
  } = usePagination(filteredBookings, 10);

  const handleStatusFilterChange = useCallback((status) => {
    setSelectedStatusFilter(status);
  }, []);

  const handleClearFilters = () => {
    setFromDate(new Date());
    setToDate(new Date());
    setSelectedStatusFilter("");
    setSearchValue("");
  };

  return {
    // Data
    bookings: paginatedBookings, // Paginated bookings for display
    allBookings: filteredBookings, // All filtered bookings for pagination component
    loading,
    error,

    // Search & Filters
    searchValue,
    handleSearch,
    fromDate,
    toDate,
    handleDateChange,

    // Modal Management
    selectedBooking,
    isModalOpen,
    handleViewBooking,
    handleCloseModal,

    // Actions
    handleDownloadMIS,
    loadBookingsData,

    // Pagination
    currentPage,
    handlePageChange,
    selectedStatusFilter,
    handleStatusFilterChange,
    handleClearFilters,
  };
};
